import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';

import { formFieldInStack } from './formFieldStackContext';
import { FormRadioOptionPressable } from './FormRadioOptionPressable';
import {
  ViewOnlyFieldValue,
  resolveIsViewOnlyPresentation,
  resolveViewOnlyDisplayText,
} from './viewOnlyFieldUtils';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, fontWeight } from '../../theme/typography';
import { AlertCircle } from '../icons';

export interface FormTwoOptionChoiceProps {
  label: string;
  value: string;
  /** Exactly two string labels (e.g. Raised / Slab). */
  options: readonly [string, string];
  onSelect: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  /** When false with `disabled`, keeps legacy read-only chrome instead of view-only text. */
  editable?: boolean;
  /** View-only request mode: plain text / dash instead of radio controls. */
  readOnlyMode?: boolean;
  error?: string;
  helperText?: string;
  /** When true, label is not shown (e.g. label + (i) rendered by parent). */
  hideLabel?: boolean;
  /**
   * Label and options on one row (baseline-aligned; wraps on narrow viewports).
   * Ignored when {@link hideLabel} is true. Matches {@link FormYesNoChoice} `inline`.
   */
  inline?: boolean;
  containerStyle?: object;
  testID?: string;
}

const optionToTestIdSuffix = (opt: string): string =>
  opt.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * Two-option radio group (same look as `FormYesNoChoice`). For fixed pairs like flooring type.
 */
export const FormTwoOptionChoice: React.FC<FormTwoOptionChoiceProps> = ({
  label,
  value,
  options,
  onSelect,
  required = false,
  disabled = false,
  editable = true,
  readOnlyMode = false,
  error,
  helperText,
  hideLabel = false,
  inline = false,
  containerStyle,
  testID,
}) => {
  const [left, right] = options;
  const isViewOnlyPresentation = resolveIsViewOnlyPresentation({
    readOnlyMode,
    disabled,
    editable,
  });

  if (isViewOnlyPresentation) {
    const displayText =
      value === left || value === right
        ? value
        : resolveViewOnlyDisplayText(value);
    return (
      <ViewOnlyFieldValue
        label={label}
        displayText={displayText}
        hideLabel={hideLabel}
        testID={testID}
        valueTestID={testID ? `${testID}-value` : 'view-only-field-value'}
        containerStyle={[containerStyle, formFieldInStack]}
        error={error}
        helperText={helperText}
      />
    );
  }

  const isControlDisabled = disabled || readOnlyMode;

  const renderRadios = (pairWithLegendInline: boolean) => (
    <>
      {[left, right].map(opt => {
        const isSelected = value === opt;
        return (
          <FormRadioOptionPressable
            key={opt}
            optionLabel={opt}
            selected={isSelected}
            disabled={isControlDisabled}
            onPress={() => onSelect(opt)}
            testID={
              testID ? `${testID}-${optionToTestIdSuffix(opt)}` : undefined
            }
            pairWithLegendInline={pairWithLegendInline}
          />
        );
      })}
    </>
  );

  const footer = (
    <>
      {error ? (
        <View style={styles.errorRow}>
          <AlertCircle
            size={14}
            color={colors.error}
            style={styles.errorRowIcon}
          />
          <Text style={styles.errorText} accessibilityRole="text">
            {error}
          </Text>
        </View>
      ) : null}
      {helperText && !error ? (
        <Text style={styles.helperText} accessibilityRole="text">
          {helperText}
        </Text>
      ) : null}
    </>
  );

  const radioA11yLabel = label + (required ? ' required' : '');

  if (inline) {
    return (
      <View
        style={[
          styles.formGroup,
          hideLabel && styles.formGroupBesideExternalLegend,
          containerStyle,
        ]}
        testID={testID}
      >
        {hideLabel ? (
          <View
            style={styles.radiosRowInline}
            accessibilityRole="radiogroup"
            accessibilityLabel={radioA11yLabel}
          >
            {renderRadios(true)}
          </View>
        ) : (
          <View style={styles.inlineAllRow}>
            <Text
              style={styles.labelInline}
              accessible={false}
              accessibilityElementsHidden
              importantForAccessibility="no"
            >
              {label}
              {required ? <Text style={styles.required}> *</Text> : null}
            </Text>
            <View
              style={styles.radiosRowInline}
              accessibilityRole="radiogroup"
              accessibilityLabel={radioA11yLabel}
            >
              {renderRadios(true)}
            </View>
          </View>
        )}
        {footer}
      </View>
    );
  }

  if (hideLabel) {
    /** Beside an external legend (`FormFieldLabelWithInfo`): compact inline radios, same band as inputs. */
    return (
      <View
        style={[
          styles.formGroup,
          styles.formGroupBesideExternalLegend,
          containerStyle,
        ]}
        testID={testID}
      >
        <View
          style={styles.radiosRowInline}
          accessibilityRole="radiogroup"
          accessibilityLabel={radioA11yLabel}
        >
          {renderRadios(true)}
        </View>
        {footer}
      </View>
    );
  }

  return (
    <View style={[styles.formGroup, containerStyle]} testID={testID}>
      <View style={styles.labelRow}>
        <Text style={styles.label}>
          {label}
          {required ? <Text style={styles.required}> *</Text> : null}
        </Text>
      </View>
      <View
        style={styles.radiosRow}
        accessibilityRole="radiogroup"
        accessibilityLabel={radioA11yLabel}
      >
        {renderRadios(false)}
      </View>
      {footer}
    </View>
  );
};

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: spacing.formFieldGroupGap,
  },
  /**
   * Legend rendered as a sibling (e.g. `FormFieldLabelWithInfo` + `hideLabel`). Without this, the
   * web flex row can stretch this wrapper and pin radios to the trailing edge.
   */
  formGroupBesideExternalLegend: {
    alignSelf: 'flex-start',
    flexGrow: 0,
    flexShrink: 0,
    alignItems: 'flex-start',
    marginBottom: 0,
    ...Platform.select({
      web: {
        width: 'auto' as const,
        maxWidth: '100%' as const,
      },
      default: {},
    }),
  },
  labelRow: {
    marginBottom: spacing.fieldVerticalGap,
  },
  label: {
    fontSize: fontSize.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
  },
  required: {
    color: colors.error,
  },
  radiosRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: spacing.formRadioGroupColumnGap,
    rowGap: spacing.sm,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 4,
    gap: 6,
  },
  errorRowIcon: {
    marginTop: 1,
  },
  errorText: {
    fontSize: 12,
    color: colors.error,
    flex: 1,
  },
  helperText: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 4,
  },
  /** Label + options on one line; wraps on narrow viewports. */
  inlineAllRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
    columnGap: spacing.sm,
    rowGap: spacing.sm,
    ...Platform.select({
      web: {
        justifyContent: 'flex-start',
      },
      default: {},
    }),
  },
  labelInline: {
    fontSize: fontSize.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
    marginRight: spacing.sm,
    flexGrow: 0,
    flexShrink: 1,
  },
  radiosRowInline: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: spacing.formRadioGroupColumnGap,
    flexShrink: 0,
  },
});
